/**
 * Offer clicks -> dataLayer events, in Frandroid's vocabulary, so the shared
 * @humanoidfr/scripts bridge sends them to PostHog as `Click Offer`.
 *
 * Replaces the Amplitude `Click Offer` tags of GTM-NLVF5C8, case for case:
 * - links tagged with data-gtm-* (comparator cards, featured offers): `comparoclic`;
 * - untagged "Voir l'offre" buttons in editorial content: `afficlic`, bloc `button_voirloffre`;
 * - the takeover (`.bg-pub-habillage`): `afficlic`, bloc `habillage`.
 * The `be-called` (Call DDR) tag is not carried over: no page renders that class.
 */

/** What is read off a clicked link; an HTMLAnchorElement satisfies it. */
export interface OfferLink {
  href: string;
  className: string;
  textContent: string | null;
  getAttribute(name: string): string | null;
}

export type OfferClickEvent = Record<string, string | null>;

// The GTM variable "nom offre": the slug of a lemon.fr/offres/<slug>/ URL.
const OFFER_SLUG = /^https:\/\/lemon\.fr\/offres\/([^/]+)\/.*$/i;
const VOIR_L_OFFRE = /Voir l['’]offre/;

// Every key of the bridge's comparoclic/afficlic whitelists is pushed, null when
// unknown: the bridge resolves a missing key against earlier pushes, as GTM
// does, and would otherwise lend this click the seller of a previous one.
function offerEvent(event: string, values: Partial<OfferClickEvent>): OfferClickEvent {
  return {
    event,
    offer_url: null,
    nom_marchand: null,
    nom_produit: null,
    position_marchand: null,
    module_produit: null,
    categorie_produit: null,
    zone: null,
    best_product_id: null,
    ...(event === 'afficlic' ? { texte: null } : {}),
    ...values,
  };
}

function offerSlug(url: string): string | null {
  return OFFER_SLUG.exec(url)?.[1] ?? null;
}

export function offerClickEvent(link: OfferLink): OfferClickEvent | null {
  const blocType = link.getAttribute('data-gtm-bloc-type');
  if (blocType) {
    return offerEvent('comparoclic', {
      offer_url: link.getAttribute('data-gtm-offer-url'),
      nom_marchand: link.getAttribute('data-gtm-seller'),
      nom_produit: link.getAttribute('data-gtm-product-name'),
      position_marchand: link.getAttribute('data-gtm-position-marchand'),
      module_produit: blocType,
      categorie_produit: link.getAttribute('data-gtm-product-category'),
      zone: link.getAttribute('data-gtm-zone'),
    });
  }

  if (link.className.split(/\s+/).includes('bg-pub-habillage')) {
    return offerEvent('afficlic', { offer_url: link.href, nom_produit: offerSlug(link.href), module_produit: 'habillage' });
  }

  if (VOIR_L_OFFRE.test(link.textContent ?? '')) {
    return offerEvent('afficlic', {
      offer_url: link.href,
      nom_produit: offerSlug(link.href),
      module_produit: 'button_voirloffre',
    });
  }

  return null;
}

let initialized = false;

/**
 * Pushes an offer click to the dataLayer. Capture phase: the clickref delegation
 * prevents the default and opens the offer itself, and a component may stop the
 * propagation -- neither must hide the click from tracking.
 */
export function initOfferClickTracking(): void {
  if (initialized || typeof document === 'undefined') return;
  document.addEventListener('click', (event) => {
    const anchor = event.target instanceof Element ? event.target.closest('a') : null;
    const offerClick = anchor ? offerClickEvent(anchor) : null;
    if (!offerClick) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(offerClick);
  }, true);
  initialized = true;
}
